import { getIsCypressOnLocalhost } from '@sureapp/platform-sdk';
import camelCase from 'lodash/camelCase';
import compact from 'lodash/compact';
import each from 'lodash/each';
import get from 'lodash/get';
import isString from 'lodash/isString';
import split from 'lodash/split';
import { geocodeByAddress } from 'react-places-autocomplete';

/**
 * Normalize a Google Places Suggestion object from this:
 *   [
 *     {
 *       long_name: 'United States',
 *       short_name: 'US',
 *       types: ['country'],
 *     },
 *     // etc.
 *   ]
 *
 * To a our standard object:
 * {
 *   line1: '123 Mulberry Lane',
 *   city: 'Brooklyn',
 *   region: {
 *     code: 'NY',
 *     name: 'New York',
 *   },
 *   postal: '11211',
 *   country: {
 *     code: 'US',
 *     name: 'United States',
 *   },
 * }
 *
 * @param {Object} googleAddress The Google place result object
 */
export function normalizeGoogleAddress(googleAddress = {}, isAlpha2 = false) {
  const addressComponents = googleAddress.address_components;
  const addressTypesMap = addressComponents.reduce(
    (result, addressComponent) => {
      each(addressComponent.types, (type) => {
        result[camelCase(type)] = {
          longName: addressComponent.long_name,
          shortName: addressComponent.short_name,
        };
      });
      return result;
    },
    {}
  );

  const streetNumber = get(addressTypesMap, 'streetNumber.longName');
  const streetAddress = get(addressTypesMap, 'route.longName');
  const city = get(
    addressTypesMap.locality ||
      addressTypesMap.sublocalityLevel1 ||
      addressTypesMap.neighborhood ||
      addressTypesMap.administrativeAreaLevel3,
    'longName'
  );
  const region = {
    code: get(addressTypesMap, 'administrativeAreaLevel1.shortName'),
    name: get(addressTypesMap, 'administrativeAreaLevel1.longName'),
  };
  const postal = get(addressTypesMap, 'postalCode.longName');
  const county = get(addressTypesMap, 'administrativeAreaLevel2.longName');
  const country = isAlpha2
    ? {
        alpha2: get(addressTypesMap, 'country.shortName'),
        name: get(addressTypesMap, 'country.longName'),
      }
    : {
        code: get(addressTypesMap, 'country.shortName'),
        name: get(addressTypesMap, 'country.longName'),
      };

  return {
    line1: compact([streetNumber, streetAddress]).join(' '),
    city,
    region,
    postal,
    county,
    country,
  };
}

export function getGeocodedNormalizedAddress(addressObj, opts = {}) {
  const optsWithDefaults = {
    withLatLng: false,
    ...opts,
  };

  if (getIsCypressOnLocalhost()) {
    let address;
    if (isString(addressObj)) {
      address = {};
      const addressList = split(addressObj, ',');
      address.line1 = addressList[0];
      address.city = addressList[1].trim();
      address.region = {
        name: addressList[2].trim(),
        code: addressList[2].trim(),
      };
      if (addressList.length > 2) {
        address.postal = addressList[3].trim();
      }
    } else if (isString(addressObj.region)) {
      addressObj.region = {
        name: addressObj.region,
        code: addressObj.region,
      };
    }
    return Promise.resolve(address || addressObj);
  }

  const addressStr = isString(addressObj)
    ? addressObj
    : [
        addressObj.line1,
        addressObj.city,
        addressObj.region,
        addressObj.postal,
      ].join(' ');

  return geocodeByAddress(addressStr).then((geocodedAddress) => {
    if (optsWithDefaults.withLatLng) {
      const { location } = geocodedAddress[0].geometry;
      return {
        ...normalizeGoogleAddress(geocodedAddress[0], opts.isAlpha2),
        latitude: Number(location.lat().toFixed(12)),
        longitude: Number(location.lng().toFixed(12)),
      };
    }
    return normalizeGoogleAddress(geocodedAddress[0]);
  });
}
